import type { ComponentPropsOptions, ExtractPublicPropTypes } from "vue";
import { defineComponent } from "vue";
import { cn } from "@/utils/cn";
import { p } from "@/utils/props-builder";
import Skeleton from "./Skeleton";

const props = {
  /**
   * Layout variant
   * - 'list': Vertical list of cards
   * - 'grid': Grid layout (2-4 columns based on screen size)
   * @default "list"
   */
  variant: p.string<"list" | "grid">("list"),

  /**
   * Number of card skeletons to show
   * @default 3
   */
  count: p.number(3),

  /**
   * Show header section (title + description)
   * @default true
   */
  showHeader: p.boolean(true),

  /**
   * Show footer section (actions/metadata)
   * @default false
   */
  showFooter: p.boolean(false),
} satisfies ComponentPropsOptions;

export type CardSkeletonProps = ExtractPublicPropTypes<typeof props>;

/**
 * CardSkeleton - Loading placeholder for Card-based layouts
 *
 * Provides skeletons for both list and grid card layouts.
 * Useful for dashboard widgets, content cards, and entity lists.
 *
 * @example
 * ```tsx
 * // List of cards
 * <CardSkeleton count={4} />
 *
 * // Grid layout
 * <CardSkeleton variant="grid" count={6} />
 *
 * // With footer
 * <CardSkeleton showFooter count={3} />
 *
 * // With QueryLoader
 * <QueryLoader queries={[dashboardQuery]}>
 *   <QueryLoader.Loading>
 *     <CardSkeleton variant="grid" count={4} />
 *   </QueryLoader.Loading>
 *   <div class="grid grid-cols-2 gap-4">
 *     {items.map(item => <Card>...</Card>)}
 *   </div>
 * </QueryLoader>
 * ```
 */
const CardSkeleton = defineComponent({
  name: "CardSkeleton",
  props,
  setup(props) {
    const containerClass = cn(
      "w-full",
      props.variant === "grid"
        ? "grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
        : "flex flex-col gap-4"
    );

    const CardItem = () => (
      <div class="rounded-lg border bg-card p-6 shadow-xs">
        {/* Header */}
        {props.showHeader && (
          <div class="mb-4 space-y-3">
            <Skeleton class="h-6 w-3/4 rounded" />
            <Skeleton class="h-4 w-full rounded" />
            <Skeleton class="h-4 w-5/6 rounded" />
          </div>
        )}

        {/* Content area */}
        <div class="space-y-2">
          <Skeleton class="h-4 w-full rounded" />
          <Skeleton class="h-4 w-4/5 rounded" />
          <Skeleton class="h-4 w-3/5 rounded" />
        </div>

        {/* Footer */}
        {props.showFooter && (
          <div class="mt-4 flex items-center justify-between border-t pt-4">
            <Skeleton class="h-4 w-24 rounded" />
            <div class="flex gap-2">
              <Skeleton class="h-8 w-20 rounded" />
              <Skeleton class="h-8 w-20 rounded" />
            </div>
          </div>
        )}
      </div>
    );

    return () => (
      <div class={containerClass}>
        {Array.from({ length: props.count }).map((_, i) => (
          <CardItem key={`card-skeleton-${i}`} />
        ))}
      </div>
    );
  },
});

export default CardSkeleton;
